import { CheckCircle, TrendingUp, XCircle, Zap } from 'lucide-react'
import type { WorkDashboard } from '../../types/work'

interface WorkDashboardStatsProps {
  stats: WorkDashboard
}

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: string | number
  sub?: string
  color?: string
}

function StatCard({ icon, label, value, sub, color = 'var(--primary, #6366f1)' }: StatCardProps) {
  return (
    <div
      style={{
        padding: '16px',
        borderRadius: '12px',
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)' }}>{label}</span>
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `${color}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
          }}
        >
          {icon}
        </div>
      </div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)' }}>{sub}</div>
      )}
    </div>
  )
}

export default function WorkDashboardStats({ stats }: WorkDashboardStatsProps) {
  const successRate = stats.total_tasks > 0
    ? Math.round((stats.completed / stats.total_tasks) * 100)
    : 0

  // Category bar chart
  const categoryEntries = Object.entries(stats.tasks_by_category)
  const maxCategoryCount = Math.max(...categoryEntries.map(([, v]) => v), 1)

  // Daily activity bar chart (last 7 days)
  const dailyEntries = Object.entries(stats.daily_activity).slice(-7)
  const maxDaily = Math.max(...dailyEntries.map(([, v]) => v), 1)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* 4 stat cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px',
        }}
      >
        <StatCard
          icon={<TrendingUp size={16} />}
          label="Total Jobs"
          value={stats.total_tasks}
          sub={`${stats.pending + stats.processing} active`}
        />
        <StatCard
          icon={<CheckCircle size={16} />}
          label="Completed"
          value={stats.completed}
          sub={`${successRate}% success rate`}
          color="var(--success, #10b981)"
        />
        <StatCard
          icon={<XCircle size={16} />}
          label="Failed"
          value={stats.failed}
          sub={stats.failed > 0 ? 'FLZ refunded' : 'All good'}
          color="var(--danger, #ef4444)"
        />
        <StatCard
          icon={<Zap size={16} />}
          label="FLZ Spent"
          value={`${stats.total_flz_spent.toFixed(1)}`}
          sub={`This week: ${stats.flz_this_week?.toFixed(1) ?? '0'} FLZ`}
          color="var(--warning, #f59e0b)"
        />
      </div>

      {/* Category distribution + daily activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        {/* Category distribution */}
        {categoryEntries.length > 0 && (
          <div
            style={{
              padding: '16px',
              borderRadius: '12px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
              By Category
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {categoryEntries.map(([cat, count]) => (
                <div key={cat}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                      {cat.replace(/_/g, ' ')}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)' }}>{count}</span>
                  </div>
                  <div
                    style={{
                      height: '4px',
                      borderRadius: '2px',
                      background: 'var(--border-color)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${(count / maxCategoryCount) * 100}%`,
                        borderRadius: '2px',
                        background: 'var(--primary, #6366f1)',
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily activity */}
        {dailyEntries.length > 0 && (
          <div
            style={{
              padding: '16px',
              borderRadius: '12px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
              Last 7 Days
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '6px',
                height: '60px',
              }}
            >
              {dailyEntries.map(([date, count]) => {
                const barHeight = maxDaily > 0 ? Math.max(4, (count / maxDaily) * 60) : 4
                const label = new Date(date).toLocaleDateString('en-US', { weekday: 'short' })
                return (
                  <div
                    key={date}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <div
                      title={`${date}: ${count} jobs`}
                      style={{
                        width: '100%',
                        height: `${barHeight}px`,
                        borderRadius: '3px 3px 0 0',
                        background: count > 0 ? 'var(--primary, #6366f1)' : 'var(--border-color)',
                        transition: 'height 0.3s ease',
                      }}
                    />
                    <span style={{ fontSize: '9px', color: 'var(--text-muted, #6b7280)' }}>{label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
